import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ProposalsPage from "@/pages/ProposalsPage";
import ClientsPage from "@/pages/ClientsPage";
import EmployeesPage from "@/pages/EmployeesPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import TasksPage from "@/pages/TasksPage";
import DocumentsPage from "@/pages/DocumentsPage";
import CalendarPage from "@/pages/CalendarPage";
import ProfilePage from "@/pages/ProfilePage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import PropertiesPage from "@/pages/PropertiesPage";
import PropertyProfilePage from "@/pages/PropertyProfilePage";
import TaskLibraryPage from "@/pages/TaskLibraryPage";
import WorkQueuePage from "@/pages/WorkQueuePage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-background"><div className="animate-pulse text-primary font-heading text-lg">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Redirect legacy standalone routes to the consolidated Properties tabs, preserving ?open=
function LegacyTabRedirect({ tab }) {
  const [sp] = useSearchParams();
  const open = sp.get('open');
  const qs = open ? `?tab=${tab}&open=${open}` : `?tab=${tab}`;
  return <Navigate to={`/properties${qs}`} replace />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-background"><div className="animate-pulse text-primary font-heading text-lg">Loading...</div></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="work-queue" element={<WorkQueuePage />} />
        <Route path="proposals" element={<ProposalsPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="properties" element={<PropertiesPage />} />
        <Route path="properties/:id" element={<PropertyProfilePage />} />
        <Route path="settings/task-library" element={<TaskLibraryPage />} />
        {/* Consolidated: Violations & DOT Permits now live as tabs on Properties */}
        <Route path="violations" element={<LegacyTabRedirect tab="violations" />} />
        <Route path="dot-permits" element={<LegacyTabRedirect tab="dot-permits" />} />
        <Route path="contacts" element={<PlaceholderPage title="Contacts" description="A unified contacts directory will live here." />} />
        <Route path="activity-log" element={<PlaceholderPage title="Activity Log" description="System activity and audit history will live here." />} />
      </Route>
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors closeButton />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ConfirmProvider>
            <AppRoutes />
            <ThemedToaster />
          </ConfirmProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
